import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Divider,
  Link,
  Paper,
  Typography,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import SettingsIcon from '@mui/icons-material/Settings';
import { Helmet } from 'react-helmet';
import { useBookMyOpAuth } from '../contexts/BookMyOpAuthContext';

const SUPPORT_EMAIL = 'support@easyhealthdigital.com';

const DeleteAccount = () => {
  const navigate = useNavigate();
  const { isAuthenticated, user } = useBookMyOpAuth();

  const userHint =
    user?.id != null || user?.mobile_number
      ? `\n\nRegistered phone (if applicable): ${user?.mobile_number || '—'}\nUser ID (if shown in app): ${user?.id ?? '—'}`
      : '';

  const mailtoHref = `mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent(
    'BookMyOP — Request to delete my account'
  )}&body=${encodeURIComponent(
    'I want to permanently delete my BookMyOP account and associated personal data.\n\nPlease confirm once deletion is complete.' +
      userHint
  )}`;

  return (
    <>
      <Helmet>
        <title>Delete account | BookMyOP</title>
        <meta
          name="description"
          content="How to delete your BookMyOP account and personal data. Request account deletion by email or from the app."
        />
      </Helmet>

      <Container maxWidth="md" sx={{ py: { xs: 3, md: 5 }, px: 2 }}>
        <Paper elevation={2} sx={{ p: { xs: 3, md: 5 } }}>
          <Typography variant="h3" component="h1" gutterBottom sx={{ fontWeight: 700 }}>
            Delete your account
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            You can ask us to delete your BookMyOP account and the personal data we hold for that account. This page
            explains what is removed and how to submit a request.
          </Typography>

          <Divider sx={{ my: 3 }} />

          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            What we delete
          </Typography>
          <Box component="ul" sx={{ pl: 2.5, m: 0, mb: 3, color: 'text.secondary', '& li': { mb: 1 } }}>
            <li>Your profile and account credentials associated with BookMyOP.</li>
            <li>App preferences and data stored for your user ID, subject to legal or medical record retention rules where applicable.</li>
            <li>
              Some information may need to be kept for a limited period where the law requires (for example billing or regulatory
              records). We will tell you if that applies to your request.
            </li>
          </Box>

          <Typography variant="h5" component="h2" gutterBottom sx={{ fontWeight: 600 }}>
            How to request deletion
          </Typography>
          <Typography variant="body1" color="text.secondary" paragraph>
            <strong>Option 1 — Email (works without signing in).</strong> Send an email from an address you can access, and include
            the phone number you use to log in to BookMyOP so we can verify ownership. We will process your request within a
            reasonable time (typically within 30 days) and confirm by email.
          </Typography>

          <Button
            variant="contained"
            color="error"
            startIcon={<EmailIcon />}
            href={mailtoHref}
            sx={{ mb: 3, textTransform: 'none' }}
          >
            Email {SUPPORT_EMAIL}
          </Button>

          <Typography variant="body1" color="text.secondary" paragraph>
            <strong>Option 2 — From the app.</strong> Open BookMyOP, go to{' '}
            <Link component={RouterLink} to="/settings">
              Settings
            </Link>
            , then use the link below to open the same email request with your details pre-filled (sign in first if you use this
            option).
          </Typography>

          {isAuthenticated && (
            <Button
              variant="outlined"
              startIcon={<SettingsIcon />}
              onClick={() => navigate('/settings')}
              sx={{ mr: 2, mb: 2, textTransform: 'none' }}
            >
              Go to Settings
            </Button>
          )}

          <Divider sx={{ my: 3 }} />

          <Typography variant="body2" color="text.secondary" paragraph>
            For more on how we handle personal data, see our{' '}
            <Link component={RouterLink} to="/privacy-policy">
              Privacy Policy
            </Link>
            .
          </Typography>

          <Typography variant="caption" display="block" color="text.secondary">
            BookMyOP — account deletion requests: {SUPPORT_EMAIL}
          </Typography>
        </Paper>
      </Container>
    </>
  );
};

export default DeleteAccount;
